import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class FreeShippingProgress extends HTMLElement {
  #threshold = 0;
  #moneyFormat = '${{amount}}';
  #currency = 'USD';
  #fillEl = null;
  #statusEl = null;
  #awayTemplate = '{amount} away!';
  #qualifiedLabel = 'You qualify for free shipping!';

  connectedCallback() {
    this.#threshold = parseInt(this.dataset.threshold || '0', 10);
    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;
    this.#currency = this.dataset.currency || this.#currency;
    this.#awayTemplate = this.dataset.awayTemplate || this.#awayTemplate;
    this.#qualifiedLabel = this.dataset.qualifiedLabel || this.#qualifiedLabel;
    this.#fillEl = this.querySelector('[data-fill]');
    this.#statusEl = this.querySelector('[data-status]');

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = async (event) => {
    const resource = event.detail?.resource;
    let subtotal = typeof resource?.items_subtotal_price === 'number' ? resource.items_subtotal_price : null;

    if (subtotal == null) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        subtotal = cart.items_subtotal_price;
      } catch {
        return;
      }
    }

    this.#render(subtotal);
  };

  #render(subtotal) {
    if (typeof subtotal !== 'number' || this.#threshold <= 0) return;

    const remaining = Math.max(0, this.#threshold - subtotal);
    const percent = Math.min(100, (subtotal / this.#threshold) * 100);
    const qualified = remaining === 0;

    if (this.#fillEl) this.#fillEl.style.width = `${percent}%`;
    this.querySelector('[data-track]')?.setAttribute('aria-valuenow', String(Math.round(percent)));
    if (this.#statusEl) {
      // innerHTML (not textContent) so an away template containing markup
      // (e.g. a <strong> around the amount) renders on live cart updates.
      this.#statusEl.innerHTML = qualified
        ? this.#qualifiedLabel
        : this.#awayTemplate.replace('{amount}', formatMoney(remaining, this.#moneyFormat, this.#currency));
    }
    // Crossing the threshold during this page view (not merely arriving on a
    // page already qualified) is the purchase-intent signal worth tracking.
    // Multiple meter instances (drawer + highlight strip) all transition on
    // the same cart update; the helper's `once` key collapses them to one event.
    if (qualified && this.dataset.qualified === 'false') {
      window.publishStorefrontEvent?.(
        'shipping_threshold_reached',
        { threshold: this.#threshold },
        { once: 'shipping_threshold_reached' }
      );
    }
    this.dataset.qualified = qualified ? 'true' : 'false';
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
